import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import { LazyLoad, EnableLazyLoad } from '../../components/LazyLoad';
import { ModuleSection } from '../../components/ModuleSection';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { GuidePageQuery, GuidePageQueryVariables } from '../../generated/types';
import {
  HAS_ACCESS_TO_DISPLAY_PAGE_FRAGMENT,
  useHasAccessToDisplayPage
} from '../../hooks/useHasAccessToDisplayPage';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useUser } from '../../hooks/useUser';
import { useRuleVariables } from '../../hooks/useRuleVariables';
import { useShowSaleOnPLP } from '../../hooks/useShowSaleOnPLP';
import { findSectionModules } from '../../lib/findSectionModules';
import { FacadePageType } from '../../lib/getRuleContextFromSlug';
import { tc } from '../../lib/trackingContext';
import { NotFoundPage } from '../NotFoundPage';
import { useGuidePageRestrictions } from './useGuidePageRestrictions';

import './GuidePage.scss';

export const GUIDE_PAGE_QUERY: TypedDocumentNode<GuidePageQuery, GuidePageQueryVariables> = gql`
  query GuidePageQuery(
    $after: String!
    $attributes: [String]
    $availability: [AvailabilityInput]
    $categoryPath: String
    $countryCode: String
    $designers: [String]
    $dynamicReRanking: Boolean
    $filterColors: [String]
    $materials: [String]
    $noSale: Boolean
    $onSale: Boolean
    $priceRange: PriceRange
    $ruleContexts: [String]
    $rules: [Rule]
    $markdownPercentages: [String]
    $scoring: String
    $sizes: [String]
    $slug: String!
    $versionId: Int
  ) {
    guideResult: guide_page(
      slug: $slug
      input: {
        attributes: $attributes
        availability: $availability
        category_path: $categoryPath
        country_code: $countryCode
        designers: $designers
        dynamic_reranking: $dynamicReRanking
        filter_colors: $filterColors
        materials: $materials
        no_sale: $noSale
        on_sale: $onSale
        price_range: $priceRange
        rule_contexts: $ruleContexts
        rules: $rules
        rounded_markdown_percentages: $markdownPercentages
        scoring: $scoring
        sizes: $sizes
        version_id: $versionId
      }
    ) {
      pageName: page_name
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
    displayPage(slug: $slug, version_id: $versionId) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
      guideSubPages {
        name
        slug
      }
      meta_description
      ...HasAccessToDisplayPageFragment
    }
  }
  ${PUMO_MODULE_FRAGMENT}
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
  ${HAS_ACCESS_TO_DISPLAY_PAGE_FRAGMENT}
`;

// TODO: categoryPath in SearchApi does not seem to correctly filter products
export const GuidePage: React.FC = () => {
  const { restricted } = useGuidePageRestrictions();

  const showSaleInPLPs = useShowSaleOnPLP();
  const { queryVariables } = usePLPParams();
  const ruleVariables = useRuleVariables(FacadePageType.Guide, queryVariables.slug);
  const {
    data: currentData,
    previousData,
    error,
    loading
  } = useQuery(GUIDE_PAGE_QUERY, {
    variables: {
      ...queryVariables,
      noSale: !showSaleInPLPs,
      ...ruleVariables
    }
  });

  const data = currentData ?? previousData;

  const { hasAccess, loading: loadingAccess } = useHasAccessToDisplayPage(data?.displayPage);

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const headerText = data?.guideResult?.pageName || '';
  const description = `Shop an edit of ${headerText} at Moda Operandi today from the latest designer fashion.`;

  const hasSlides = headerModules?.modules.some(
    module =>
      module.__typename === 'SinglePumoModule' &&
      module.slides?.some(slide => slide.featureImage || slide.mobileImage || slide.slideText)
  );

  useEffect(() => {
    tc.setCurrent('guide_page_name', headerText);
    return () => {
      tc.setCurrent('guide_page_name', undefined);
    };
  });

  const { user } = useUser();

  if (
    data?.displayPage?.slug === 'vip-bottega-store' &&
    (!user.isLoggedIn || !user.email?.endsWith('@modaoperandi.com'))
  )
    return <NotFoundPage />;

  if (data && (!data.guideResult || !data.displayPage)) return <NotFoundPage />;

  if (restricted || !hasAccess) return <NotFoundPage />;

  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}
      {headerModules && <ModuleSection modules={headerModules.modules} />}

      <ProductListing
        title={headerText}
        description={description}
        headerText={!headerModules || !hasSlides ? headerText : ''}
        queryResult={data?.guideResult}
        displayPage={data?.displayPage}
        error={error}
        loading={loading || loadingAccess}
        belowHeader={
          <>
            {data?.displayPage?.meta_description && (
              <div className="GuidePage__metaDescription">
                {data?.displayPage?.meta_description}
              </div>
            )}
          </>
        }
      />

      {bottomModules && (
        <EnableLazyLoad>
          <LazyLoad>
            <ModuleSection modules={bottomModules.modules} />
          </LazyLoad>
        </EnableLazyLoad>
      )}
    </>
  );
};
